import {
  type MessageFormatElement,
  isArgumentElement,
  isDateElement,
  isLiteralElement,
  isNumberElement,
  isPluralElement,
  isSelectElement,
  isTagElement,
  isTimeElement,
  parse,
} from '@formatjs/icu-messageformat-parser'
import type {Node} from 'estree-jsx'
import type {Rule} from 'eslint'
import {
  extractMessages,
  getSettings,
} from '#packages/eslint-plugin-formatjs/util.js'
import {
  type CoreMessageIds,
  CORE_MESSAGES,
} from '#packages/eslint-plugin-formatjs/messages.js'

type MessageIds = 'blocklist' | CoreMessageIds

export const name = 'blocklist-elements'

function getMessage(type: Element): {
  messageId: MessageIds
  data: Record<string, Element>
} {
  return {
    messageId: 'blocklist',
    data: {type},
  }
}

export enum Element {
  literal = 'literal',
  argument = 'argument',
  number = 'number',
  date = 'date',
  time = 'time',
  select = 'select',
  selectordinal = 'selectordinal',
  plural = 'plural',
  tag = 'tag',
}

function verifyAst(blocklist: Element[], ast: MessageFormatElement[]) {
  const errors: ReturnType<typeof getMessage>[] = []
  for (const el of ast) {
    if (isLiteralElement(el) && blocklist.includes(Element.literal)) {
      errors.push(getMessage(Element.literal))
    }
    if (isArgumentElement(el) && blocklist.includes(Element.argument)) {
      errors.push(getMessage(Element.argument))
    }
    if (isNumberElement(el) && blocklist.includes(Element.number)) {
      errors.push(getMessage(Element.number))
    }
    if (isDateElement(el) && blocklist.includes(Element.date)) {
      errors.push(getMessage(Element.date))
    }
    if (isTimeElement(el) && blocklist.includes(Element.time)) {
      errors.push(getMessage(Element.time))
    }
    if (isSelectElement(el) && blocklist.includes(Element.select)) {
      errors.push(getMessage(Element.select))
    }
    if (isTagElement(el) && blocklist.includes(Element.tag)) {
      errors.push(getMessage(Element.tag))
    }
    if (isPluralElement(el)) {
      if (blocklist.includes(Element.plural)) {
        errors.push(getMessage(Element.argument))
      }
      if (
        el.pluralType === 'ordinal' &&
        blocklist.includes(Element.selectordinal)
      ) {
        errors.push(getMessage(Element.selectordinal))
      }
    }
    if (isSelectElement(el) || isPluralElement(el)) {
      const {options} = el
      for (const selector of Object.keys(options)) {
        verifyAst(blocklist, options[selector].value)
      }
    }
  }

  return errors
}

function checkNode(context: Rule.RuleContext, node: Node) {
  const settings = getSettings(context)
  const msgs = extractMessages(node, settings)
  if (!msgs.length) {
    return
  }

  const blocklist = context.options[0]
  if (!Array.isArray(blocklist) || !blocklist.length) {
    return
  }
  for (const [
    {
      message: {defaultMessage},
      messageNode,
    },
  ] of msgs) {
    if (!defaultMessage || !messageNode) {
      continue
    }
    let ast: MessageFormatElement[]
    try {
      ast = parse(defaultMessage, {
        ignoreTag: settings.ignoreTag,
      })
    } catch (e) {
      context.report({
        node: messageNode,
        messageId: 'parseError',
        data: {
          error: (e as Error).message,
        },
      })
      continue
    }
    const errors = verifyAst(blocklist, ast)
    for (const error of errors) {
      context.report({
        node: messageNode,
        ...error,
      })
    }
  }
}

export const rule: Rule.RuleModule = {
  meta: {
    type: 'problem',
    docs: {
      description: 'Disallow specific elements in ICU message format',
      url: 'https://formatjs.github.io/docs/tooling/linter#blocklist-elements',
    },
    fixable: 'code',
    schema: [
      {
        type: 'array',
        items: {
          type: 'string',
          enum: Object.keys(Element),
        },
      },
    ],
    messages: {
      ...CORE_MESSAGES,
      blocklist: `{{type}} element is blocklisted`,
    },
  },
  create(context) {
    const callExpressionVisitor: Rule.RuleListener['CallExpression'] = node =>
      checkNode(context, node)

    const parserServices = context.sourceCode.parserServices
    if (parserServices?.defineTemplateBodyVisitor) {
      return parserServices.defineTemplateBodyVisitor(
        {
          CallExpression: callExpressionVisitor,
        },
        {
          CallExpression: callExpressionVisitor,
        }
      )
    }
    return {
      JSXOpeningElement: node => checkNode(context, node),
      CallExpression: callExpressionVisitor,
    }
  },
}
